import { z } from 'zod';

import { REQUIRED, optionalPhone, optionalText, picked, requiredEmail } from '@/lib/validation';

/**
 * The rules for an administrator account, mirroring the admin-users and auth
 * modules of the API.
 */

/** The API's password policy: 12 to 128 characters, not all of them spaces. */
export const PASSWORD_MIN_LENGTH = 12;
const PASSWORD_MAX_LENGTH = 128;

/**
 * A password being set — on a new account or from a reset link. Counted in
 * characters as a person sees them, as the server counts them, so an emoji is
 * one and not two.
 */
export const newPassword = z.string().superRefine((value, context) => {
  const length = Array.from(value).length;
  const message =
    value === ''
      ? REQUIRED
      : length < PASSWORD_MIN_LENGTH
        ? `Use at least ${PASSWORD_MIN_LENGTH} characters.`
        : length > PASSWORD_MAX_LENGTH
          ? `Keep it to ${PASSWORD_MAX_LENGTH} characters or fewer.`
          : !/\S/u.test(value)
            ? 'A password cannot be only spaces.'
            : null;
  if (message) context.addIssue({ code: 'custom', message });
});

/** The roles a new administrator starts with, or an existing one is given. */
export const roleIdsSchema = z
  .array(z.string())
  .min(1, 'Give them at least one role.')
  .max(20, 'Up to 20 roles.');

/** The fields the PATCH contract accepts. Blank names and phone are cleared. */
export const adminUserSchema = z.object({
  email: requiredEmail,
  firstName: optionalText(100),
  lastName: optionalText(100),
  phone: optionalPhone,
  status: picked('Choose a status.'),
});

/** Creating also sets the first password and roles. */
export const newAdminUserSchema = adminUserSchema.extend({
  password: newPassword,
  roleIds: roleIdsSchema,
});

/** Setting a new password from a reset link: typed twice, the same both times. */
export const resetPasswordSchema = z
  .object({ password: newPassword, confirm: z.string().min(1, REQUIRED) })
  .refine((draft) => draft.confirm === '' || draft.confirm === draft.password, {
    path: ['confirm'],
    message: 'The two passwords do not match.',
  });
